import { config } from '@app/config';
import { ReactNode, useCallback, useMemo } from 'react';
import { NotificationToastsBridge } from '../toast/NotificationToastsBridge';
import { KnockNotificationsProvider } from './KnockNotificationsProvider';
import {
  NotificationsSessionContextProvider,
  NotificationsSessionContextValue,
} from './notifications-session-context';
import { useNotificationsSession } from './useNotificationsSession';

export type NotificationsSessionProviderProps = {
  children: ReactNode;
};

/**
 * Fetches the notifications session and mounts Knock when the provider is ready.
 * When the API returns noop, the session fails, or the feed channel id is missing,
 * children render without a Knock client.
 */
export function NotificationsSessionProvider({ children }: NotificationsSessionProviderProps) {
  const sessionQuery = useNotificationsSession();
  const session = sessionQuery.data ?? null;
  const feedChannelId = config.knockFeedChannelId;

  const isKnockReady =
    session?.provider === 'knock' &&
    Boolean(session.token) &&
    Boolean(session.publicApiKey) &&
    Boolean(feedChannelId);

  const refetchSession = useCallback(async () => {
    const result = await sessionQuery.refetch();
    return result.data;
  }, [sessionQuery]);

  const contextValue = useMemo<NotificationsSessionContextValue>(
    () => ({
      provider: session?.provider ?? null,
      isKnockReady,
      session,
      isLoading: sessionQuery.isLoading,
      refetchSession,
    }),
    [session, isKnockReady, sessionQuery.isLoading, refetchSession],
  );

  return (
    <NotificationsSessionContextProvider value={contextValue}>
      {isKnockReady && session && feedChannelId ? (
        <KnockNotificationsProvider session={session} feedChannelId={feedChannelId}>
          <NotificationToastsBridge />
          {children}
        </KnockNotificationsProvider>
      ) : (
        children
      )}
    </NotificationsSessionContextProvider>
  );
}
